import { ButtonBase, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import { PointDisplay } from 'utoniq-ui'

import {
  Maybe,
  SkuTagFragment,
  UserPaymentType,
} from '@/clients/utoniq-core/schema'
import { Organism } from '@/core/architecture'

type SkuTagProps = {
  skuUnit: SkuTagFragment
  active?: boolean
  disabled?: boolean
  onClick: () => void
  paymentType?: Maybe<UserPaymentType>
  issuedNumber?: number | null
  isResaleEnable?: boolean | null
}

export const SkuTag: Organism<SkuTagProps> = ({
  skuUnit,
  active,
  disabled,
  onClick,
  paymentType,
  issuedNumber,
  isResaleEnable,
}) => {
  const isSoldOut = !!(
    skuUnit.stock &&
    skuUnit.remainingNumber === 0 &&
    !isResaleEnable
  )
  const isStockNotSet = skuUnit.stock === null && issuedNumber !== null

  const { borderColor, color, bgcolor } = useMemo(() => {
    if (disabled || isStockNotSet || isSoldOut) {
      return {
        color: 'text.disabled',
        bgcolor: 'grey.100',
        borderColor: 'grey.100',
      }
    }
    if (active) {
      return {
        color: 'white',
        bgcolor: 'primary.main',
        borderColor: 'primary.main',
      }
    }
    return {
      color: 'text.secondary',
      bgcolor: 'transparent',
      borderColor: 'grey.100',
    }
  }, [active, skuUnit.remainingNumber, disabled])

  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled || isStockNotSet || isSoldOut}
      sx={{
        borderRadius: '14px',
        px: 3,
        py: 1,
        minWidth: 'fit-content',
        borderColor,
        bgcolor,
        borderWidth: 1,
        borderStyle: 'solid',
        '.MuiTypography-root': {
          color,
        },
      }}
    >
      <Stack color={color}>
        <Typography variant="subtitle1">{skuUnit.unitName}</Typography>
        {skuUnit.formattedPrice !== '0' && (
          <PointDisplay
            point={skuUnit.formattedPrice ?? 0}
            size="small"
            paymentType={paymentType ?? 'prepaid_point'}
          />
        )}
        <Typography variant="overline">
          {(() => {
            if (isSoldOut || isStockNotSet) return '×'
            if (issuedNumber === null) return '◯'
            if (!skuUnit.remainingNumber) return '×'
            return skuUnit.remainingNumber > 9 ? '◯' : '△'
          })()}
        </Typography>
      </Stack>
    </ButtonBase>
  )
}
